// Update the import line
import React, { useState, useEffect } from 'react';
// Add Spinner, Modal, Form, Button, Alert imports
import { Spinner, Modal, Form, Button, Alert } from 'react-bootstrap';
// Keep useAuth if needed for fallback or other checks, but primary data comes from API
import { useAuth } from '../../hooks/useAuth';
// Import the specific API object
import { rmApi } from '../../services/api'; // Adjust path if needed
import { FaUser, FaKey, FaBell, FaShieldAlt } from 'react-icons/fa';
import './Settings.css';

function RMSettings() {
  // const { getCurrentUser } = useAuth(); // Keep if needed for initial role check or fallback
  // const currentUser = getCurrentUser(); // Comment out or remove direct usage for details

  const [activeSection, setActiveSection] = useState('profile');
  // Add state for fetched data, loading, and errors
  const [rmDetails, setRmDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Add state for notification settings
  const [notifications, setNotifications] = useState(true);

  // Add state for reset password modal (These were missing)
  const [showResetModal, setShowResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  // --- Add Modal Handler Functions ---
  const handleCloseModal = () => {
    setShowResetModal(false);
    // Reset modal-specific states
    setNewPassword('');
    setConfirmPassword('');
    setResetError('');
    setResetSuccess('');
    setResetLoading(false);
  };

  const handlePasswordResetSubmit = async (event) => {
    event.preventDefault(); // Prevent default form submission
    setResetError('');
    setResetSuccess('');

    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 5) { // Basic validation
      setResetError('Password must be at least 5 characters long.');
      return;
    }

    setResetLoading(true);
    try {
      // TODO: Implement the actual API call to reset the password
      // Example: await userApi.resetPassword(rmDetails.id, newPassword);
      console.log("Simulating password reset for user ID:", rmDetails?.id);
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      setResetSuccess('Password reset successfully!');
      // Optionally close modal after a delay or keep it open to show success
      // setTimeout(handleCloseModal, 2000);

    } catch (err) {
      console.error("Error resetting password:", err);
      setResetError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };
  // --- End Modal Handler Functions ---

  // Navigation items for settings
  const navItems = [
    { id: 'profile', label: 'Profile Information', icon: <FaUser size={16} /> },
    { id: 'security', label: 'Security', icon: <FaKey size={16} /> },
    { id: 'notifications', label: 'Notifications', icon: <FaBell size={16} /> }
  ];
  
  // Toggle handlers for notifications
  const toggleNotifications = () => {
    setNotifications(!notifications);
  };

  // Add this useEffect hook
  useEffect(() => {
    const fetchDetails = async () => {
      // Only fetch if the profile section is initially active or always fetch on load
      // if (activeSection === 'profile') { // Optional: fetch only when profile tab is active
        setLoading(true);
        setError('');
        try {
          console.log("Attempting to fetch RM details..."); // Debug log
          const response = await rmApi.getRmDetails();
          console.log("RM details fetched:", response.data); // Debug log
          setRmDetails(response.data);
          
          // If your API returns notification preferences, set them here
          if (response.data.preferences) {
            setNotifications(response.data.preferences.notificationEnabled || true);
          }
        } catch (err) {
          console.error("Error fetching RM details:", err);
          let errorMessage = 'Failed to load profile information.';
          if (err.response) {
            // Server responded with a status code outside 2xx range
            console.error("Error response:", err.response.data);
            errorMessage += ` (Server: ${err.response.data.detail || err.response.statusText})`;
          } else if (err.request) {
            // Request was made but no response received
            console.error("Error request:", err.request);
            errorMessage += ' (No response from server)';
          } else {
            // Something else happened in setting up the request
            console.error("Error message:", err.message);
            errorMessage += ` (${err.message})`;
          }
          setError(errorMessage);
          // Optional: Fallback to local storage data if API fails
          // const fallbackUser = JSON.parse(localStorage.getItem('user'));
          // setRmDetails(fallbackUser);
        } finally {
          setLoading(false);
        }
      // }
    };

    fetchDetails();
  }, []); // Empty dependency array: runs once on mount.
          // If you want it to refetch when the profile tab is clicked, add activeSection
          // to the dependency array and uncomment the if condition inside.

  // Add this useEffect hook near the other useEffect for fetching details
  useEffect(() => {
    console.log(`showResetModal state changed to: ${showResetModal}`);
  }, [showResetModal]); // Dependency array ensures this runs only when showResetModal changes

  // Render content based on active section
  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        // Handle loading state - Replace <p> with <Spinner>
        if (loading) {
          // Use Spinner component for visual feedback
          return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          );
        }
        // Handle error state (remains the same)
        if (error) {
          return <p style={{ color: 'red' }}>Error: {error}</p>;
        }
        // Display data once loaded
        return (
          <>
            <h3>Profile Information</h3>
            {rmDetails ? ( // Check if rmDetails is not null
              <div className="rm-profile-details">
                <div className="rm-detail-item">
                  <span className="rm-detail-label">Name:</span>
                  {/* Use data from rmDetails state */}
                  <span className="rm-detail-value">{rmDetails.name || 'N/A'}</span>
                </div>
                <div className="rm-detail-item">
                  <span className="rm-detail-label">Email:</span>
                  {/* Use data from rmDetails state */}
                  <span className="rm-detail-value">{rmDetails.email || 'N/A'}</span>
                </div>
                <div className="rm-detail-item">
                  <span className="rm-detail-label">Mobile Number:</span>
                  {/* Correctly access the 'mobile' field from the API response */}
                  <span className="rm-detail-value">{rmDetails?.mobile || 'N/A'}</span>
                </div>
                <div className="rm-detail-item">
                  <span className="rm-detail-label">Status:</span>
                  {/* Assuming status comes from API or is derived */}
                  <span className="rm-detail-value">{rmDetails.is_active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
            ) : (
              // Handle case where loading is false, no error, but data is still null
              <p>Could not load profile data.</p>
            )}
          </>
        );
      case 'security':
        // Add this log
        console.log("Rendering 'security' section content.");
        return (
          <>
            <h3>Security Settings</h3>
            <div className="rm-profile-details">
              <div className="rm-detail-item">
                <span className="rm-detail-label">Password:</span>
                {/* Change the onClick handler here */}
                <button
                  className="rm-btn rm-btn-primary"
                  onClick={() => {
                      // Add this log
                      console.log("Reset Password button clicked!");
                      setShowResetModal(true); // Set state directly
                  }}
                >
                  <FaKey/> Reset Password
                </button>
              </div>
            </div>
          </>
        );
      case 'notifications':
        return (
          <>
            <h3>Notification Preferences</h3>
            <div className="rm-profile-details">
              <div className="rm-detail-item">
                <span className="rm-detail-label">Notifications:</span>
                <div className="rm-toggle-container">
                  <label className="rm-toggle">
                    <input 
                      type="checkbox" 
                      checked={notifications} 
                      onChange={toggleNotifications} 
                    />
                    <span className="rm-toggle-slider"></span>
                  </label>
                  <span className="rm-toggle-status">
                    {notifications ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          </>
        );
      default:
        return <p>Select a section from the menu</p>;
    }
  };

  return (
    <div className="rm-settings-page">
      <div className="rm-settings-content">
        {/* Left section - Navigation */}
        <div className="rm-settings-left">
          <ul className="rm-settings-nav">
            {navItems.map(item => (
              <li key={item.id} className="rm-settings-nav-item">
                <div 
                  className={`rm-settings-nav-link ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => setActiveSection(item.id)}
                >
                  <span className="rm-settings-nav-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Right section - Content */}
        <div className="rm-settings-right">
          {renderContent()}
        </div>
      </div>

      {/* Reset Password Modal with Custom Styling */}
      <Modal show={showResetModal} onHide={handleCloseModal} centered className="custom-modal">
        <Form onSubmit={handlePasswordResetSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <FaShieldAlt className="me-2" /> Reset Your Password
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Display Error/Success Messages */}
            {resetError && <Alert variant="danger">{resetError}</Alert>}
            {resetSuccess && <Alert variant="success">{resetSuccess}</Alert>}

            {/* Hide form fields after success */}
            {!resetSuccess && (
              <>
                <Form.Group className="mb-4" controlId="formNewPassword">
                  <Form.Label>New Password</Form.Label>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type="password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={5}
                    />
                    <FaKey className="input-icon text-muted" />
                  </div>
                  <Form.Text className="text-muted">
                    Password must be at least 5 characters long
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-4" controlId="formConfirmPassword">
                  <Form.Label>Confirm New Password</Form.Label>
                  <div className="password-input-wrapper">
                    <Form.Control
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={5}
                    />
                    <FaKey className="input-icon text-muted" />
                  </div>
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={resetLoading}>
              Cancel
            </Button>
            {/* Hide submit button after success */}
            {!resetSuccess && (
              <Button className="rm-btn rm-btn-primary" variant="" type="submit" disabled={resetLoading}>
                {resetLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Updating...
                  </>
                ) : (
                  'Update Password'
                )}
              </Button>
            )}
          </Modal.Footer>
        </Form>
      </Modal>

    </div> // End rm-settings-page div
  );
}

export default RMSettings;
